import { parseLocalDate } from './dateUtils';

export const MAX_CONDITION_GUARANTEE = 2500;
export const PLATFORM_FEE_PERCENT = 10;
export const OWNER_SHARE_PERCENT = 100 - PLATFORM_FEE_PERCENT;
export const PAYOUT_HOURS_BEFORE_START = 48;
/** Local SVG used when a listing has no main photo. */
export const GEAR_IMAGE_PLACEHOLDER = '/gear-placeholder.svg';

export function formatRentalStatus(status) {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return 'Unknown';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function calculateRentalDays(startDate, endDate) {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (!start || !end) return 0;
  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return Math.max(1, days);
}

export function calculateRentalTotal(pricePerDay, startDate, endDate) {
  const dailyRate = Number(pricePerDay);
  if (!Number.isFinite(dailyRate) || dailyRate <= 0) return 0;
  return dailyRate * calculateRentalDays(startDate, endDate);
}

export function isRentalPaid(rental) {
  return rental?.paymentStatus === 'paid' || rental?.paid === true;
}

export function isRentalPaymentScheduled(rental) {
  return rental?.paymentStatus === 'scheduled';
}

export function getChargeDueDate(rental) {
  if (rental?.chargeDueAt) return new Date(rental.chargeDueAt);
  return getPayoutDueDate(rental);
}

export function formatChargeDueLabel(rental) {
  const due = getChargeDueDate(rental);
  if (!due || Number.isNaN(due.getTime())) return null;
  const day = String(due.getDate()).padStart(2, '0');
  const month = due.toLocaleDateString('en-US', { month: 'short' });
  const year = String(due.getFullYear()).slice(-2);
  const timePart = due.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${day} ${month} ${year} ${timePart}`;
}

export function isRentalEnded(rental, now = new Date()) {
  const end = parseLocalDate(rental?.rentalEndDate);
  if (!end) return false;
  end.setHours(23, 59, 59, 999);
  return now > end;
}

export function isRentalPayable(rental) {
  if (!rental) return false;
  if (rental.status !== 'approved') return false;
  if (isRentalPaid(rental)) return false;
  if (isRentalPaymentScheduled(rental)) return false;
  if (isRentalCancelled(rental)) return false;
  if (isRentalEnded(rental)) return false;
  return true;
}

export function getRenterPayableRentals(rentals, userId) {
  return (rentals || []).filter(
    (rental) => rental.renterId === userId && isRentalPayable(rental)
  );
}

export function selectRenterPaymentRental(rentals, userId, preferredRentalId) {
  const mine = (rentals || []).filter(
    (rental) => rental.renterId === userId && rental.status === 'approved'
  );

  if (preferredRentalId) {
    const preferred = mine.find((rental) => rental.id === preferredRentalId);
    if (preferred) return preferred;
  }

  const payable = getRenterPayableRentals(rentals, userId);
  return payable.sort(
    (a, b) => parseLocalDate(a.rentalStartDate) - parseLocalDate(b.rentalStartDate)
  )[0] || null;
}

/** @deprecated Use selectRenterPaymentRental */
export const selectRenterPayableRental = selectRenterPaymentRental;

export function canRenterPay({ user, gear, rental }) {
  if (!user || !rental || !gear) return false;
  if (user.uid !== rental.renterId) return false;
  if (user.uid === gear.ownerId) return false;
  return isRentalPayable(rental);
}

export function getRentalPaymentAmount(rental, gear) {
  const storedTotal = Number(rental?.totalAmount);
  if (Number.isFinite(storedTotal) && storedTotal > 0) {
    return storedTotal;
  }

  const dailyRate = Number(rental?.pricePerDay ?? gear?.pricePerDay);
  if (Number.isFinite(dailyRate) && dailyRate > 0) {
    return calculateRentalTotal(
      dailyRate,
      rental?.rentalStartDate,
      rental?.rentalEndDate
    );
  }

  return 0;
}

export function canShowRentalPaymentLink(rental, gear) {
  return isRentalPayable(rental) && getRentalPaymentAmount(rental, gear) >= 1;
}

export function isBookedDateRangeActive(booked, now = new Date()) {
  const end = parseLocalDate(booked?.endDate);
  if (!end) return false;
  end.setHours(23, 59, 59, 999);
  return now <= end;
}

export function filterActiveBookedDates(bookedDates, now = new Date()) {
  return (bookedDates || []).filter((booked) => isBookedDateRangeActive(booked, now));
}

export function getConditionHoldAmount(gear, rental) {
  const raw = Number(
    rental?.conditionGuaranteeAmount ?? gear?.conditionGuaranteeAmount ?? 0
  );
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  return Math.min(raw, MAX_CONDITION_GUARANTEE);
}

export function capConditionGuaranteeAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.min(amount, MAX_CONDITION_GUARANTEE);
}

export function getPayoutDueDate(rental) {
  const start = parseLocalDate(rental?.rentalStartDate);
  if (!start) return null;
  const due = new Date(start.getTime() - PAYOUT_HOURS_BEFORE_START * 60 * 60 * 1000);
  const paidAt = rental.paidAt ? new Date(rental.paidAt) : new Date();
  return due > paidAt ? due : paidAt;
}

export function formatPayoutDueLabel(rental) {
  const due = rental?.payoutDueAt
    ? new Date(rental.payoutDueAt)
    : getPayoutDueDate(rental);
  if (!due || Number.isNaN(due.getTime())) return null;
  const day = String(due.getDate()).padStart(2, '0');
  const month = due.toLocaleDateString('en-US', { month: 'short' });
  const year = String(due.getFullYear()).slice(-2);
  const timePart = due.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${day} ${month} ${year} ${timePart}`;
}

export function isOutsidePayoutWindow(rental, now = new Date()) {
  const start = parseLocalDate(rental?.rentalStartDate);
  if (!start) return false;
  const windowStart = new Date(
    start.getTime() - PAYOUT_HOURS_BEFORE_START * 60 * 60 * 1000
  );
  return now < windowStart;
}

export function isRentalNotStarted(rental, now = new Date()) {
  const start = parseLocalDate(rental?.rentalStartDate);
  if (!start) return false;
  start.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today < start;
}

export function isRentalCancelled(rental) {
  return rental?.status === 'cancelled';
}

export function canRenterCancelRental(rental, userId) {
  if (!rental || !userId || rental.renterId !== userId) return false;
  if (rental.status !== 'approved') return false;
  // Renters can cancel right up to the start date. Inside 48 hours they forfeit
  // payment, but they are never stuck unable to tell the owner they aren't coming.
  return isRentalNotStarted(rental);
}

export function canOwnerCancelRental(rental, userId) {
  if (!rental || !userId || rental.ownerId !== userId) return false;
  if (rental.status !== 'approved') return false;
  return isRentalNotStarted(rental);
}

export function getCancelRefundMessage(rental, role) {
  const paid = isRentalPaid(rental);
  const outsideWindow = isOutsidePayoutWindow(rental);

  if (isRentalPaymentScheduled(rental)) {
    return 'This will cancel the booking, free the dates, and your card will not be charged.';
  }

  if (!paid) {
    return 'This will cancel the booking and free the dates on the calendar.';
  }

  if (outsideWindow) {
    return role === 'owner'
      ? 'The renter will receive a full automatic refund.'
      : 'You will receive a full automatic refund.';
  }

  if (role === 'owner') {
    return 'This is within 48 hours of the start date and the renter has already been charged. Cancelling now refunds them in full, and that amount is reversed out of your payout. These dates stay blocked on your listing so they cannot be re-booked.';
  }

  const paidAmount =
    Number(rental?.totalPaidCents || 0) / 100 || Number(rental?.totalAmount || 0);
  const amountLabel = paidAmount > 0 ? `$${paidAmount.toFixed(2)}` : 'your payment';

  return `This is within 48 hours of the start date and ${amountLabel} has already been charged. You will NOT be refunded, and the owner keeps the payment. Cancelling does free the dates so the owner can re-rent them. If you are hoping for a refund, message the owner before cancelling.`;
}

export function canOwnerReleaseConditionHold(rental, userId) {
  if (!rental || !userId) return false;
  if (rental.ownerId !== userId) return false;
  if (!isRentalPaid(rental)) return false;
  if (rental.conditionHoldStatus !== 'authorized') return false;
  return getConditionHoldAmount(null, rental) > 0;
}

/**
 * Owner payout breakdown for a rental.
 * Prefer snapshot fields set at charge time; optional platformFeePercent override.
 */
export function getOwnerPayoutSummary(rental, options = {}) {
  const total = Number(rental?.totalPaidCents || (rental?.totalAmount || 0) * 100);
  if (!Number.isFinite(total) || total <= 0) return null;

  let feePercent = options.platformFeePercent;
  if (feePercent === undefined || feePercent === null) {
    if (rental?.platformFeePercentApplied !== undefined && rental?.platformFeePercentApplied !== null) {
      feePercent = Number(rental.platformFeePercentApplied);
    } else if (rental?.ownerPlatformFeeExempt) {
      feePercent = 0;
    } else {
      feePercent = PLATFORM_FEE_PERCENT;
    }
  }
  feePercent = Number(feePercent);
  if (!Number.isFinite(feePercent)) feePercent = PLATFORM_FEE_PERCENT;
  feePercent = Math.min(100, Math.max(0, feePercent));

  const ownerCents = Math.floor(total * ((100 - feePercent) / 100));
  return {
    ownerAmount: ownerCents / 100,
    platformAmount: (total - ownerCents) / 100,
    platformFeePercent: feePercent,
    ownerSharePercent: 100 - feePercent,
  };
}